import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UsersIcon, SendIcon, CheckCircle2Icon, MailIcon, PhoneIcon, BuildingIcon } from 'lucide-react';
type Step = 'search' | 'contacts' | 'sequence';
export function DatabaseDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [contactsPhase, setContactsPhase] = useState<'loading' | 'done'>('loading');
  const [sequencePhase, setSequencePhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const contactsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sequenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (contactsTimerRef.current) clearTimeout(contactsTimerRef.current);
      if (sequenceTimerRef.current) clearTimeout(sequenceTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchPhase('done'), 1200);
  };
  const startContacts = () => {
    setContactsPhase('loading');
    if (contactsTimerRef.current) clearTimeout(contactsTimerRef.current);
    contactsTimerRef.current = setTimeout(() => setContactsPhase('done'), 1400);
  };
  const startSequence = () => {
    setSequencePhase('loading');
    if (sequenceTimerRef.current) clearTimeout(sequenceTimerRef.current);
    sequenceTimerRef.current = setTimeout(() => setSequencePhase('done'), 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') startSearch();
    if (step === 'contacts') startContacts();
    if (step === 'sequence') startSequence();
  };
  return <section className="py-24 bg-white" data-id="element-3699">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3700">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3701">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-3702">
            Experience the{' '}
            <span className="text-blue-600" data-id="element-3703">local business database</span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-3704">
            Search, view employee email lists, and launch sequences without ever
            leaving the platform.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-3705">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-3706">
            <SearchIcon className="w-4 h-4" data-id="element-3707" />
            Search local businesses
          </button>
          <button onClick={() => handleTabSwitch('contacts')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'contacts' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-3708">
            <UsersIcon className="w-4 h-4" data-id="element-3709" />
            View owner emails
          </button>
          <button onClick={() => handleTabSwitch('sequence')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'sequence' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-3710">
            <SendIcon className="w-4 h-4" data-id="element-3711" />
            Launch sequence
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-3712">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-3713">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-3714">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3715">
                  Search local businesses
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-3716">
                  Query our local business database by industry and location.
                </p>
                <div className="space-y-5" data-id="element-3717">
                  <div data-id="element-3718">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3719">
                      Industry
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3720">
                      Dental Clinics
                    </div>
                  </div>
                  <div data-id="element-3721">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3722">
                      Location
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3723">
                      California
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-3724">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-3725">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-3726" />
                      Searching...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-3727">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-3728" />
                      4,210 businesses found
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-3729">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-3730">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-3731" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-3732">
                      Querying database...
                    </p>
                  </div>}
                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-3733">
                    <div className="flex items-center justify-between mb-6" data-id="element-3734">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-3735">
                        Results
                      </h3>
                      <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-3736">
                        4,210 found
                      </span>
                    </div>
                    <div className="space-y-3" data-id="element-3737">
                      {[{
                  name: 'Smile Bright Dental',
                  location: 'San Francisco, CA',
                  employees: '15',
                  rating: '4.8',
                  phone: '(415) 555-0123',
                  email: 'info@smilebright.com',
                  category: 'Dentist',
                  revenue: '$1.2M'
                }, {
                  name: 'Cali Coast Dentistry',
                  location: 'Los Angeles, CA',
                  employees: '28',
                  rating: '4.6',
                  phone: '(310) 555-0456',
                  email: 'hello@calicoast.com',
                  category: 'Dentist',
                  revenue: '$2.1M'
                }, {
                  name: 'Bay Area Orthodontics',
                  location: 'Oakland, CA',
                  employees: '12',
                  rating: '4.9',
                  phone: '(510) 555-0789',
                  email: 'contact@bayortho.com',
                  category: 'Orthodontist',
                  revenue: '$900K'
                }].map((biz, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-3738">
                          <div className="flex items-start justify-between mb-3" data-id="element-3739">
                            <div data-id="element-3740">
                              <h4 className="text-base font-bold text-slate-900" data-id="element-3741">
                                {biz.name}
                              </h4>
                              <div className="flex items-center gap-3 mt-1" data-id="element-3742">
                                <span className="text-xs text-slate-500" data-id="element-3743">
                                  {biz.location}
                                </span>
                                <span className="text-xs text-slate-500" data-id="element-3744">
                                  ⭐ {biz.rating}
                                </span>
                                <span className="text-xs text-slate-400" data-id="element-3745">
                                  {biz.category}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-600" data-id="element-3746">
                            <span className="flex items-center gap-1.5" data-id="element-3747">
                              <MailIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-3748" />
                              {biz.email}
                            </span>
                            <span className="flex items-center gap-1.5" data-id="element-3749">
                              <PhoneIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3750" />
                              {biz.phone}
                            </span>
                            <span className="text-slate-400" data-id="element-3751">•</span>
                            <span data-id="element-3752">{biz.employees} employees</span>
                            <span className="text-slate-400" data-id="element-3753">•</span>
                            <span data-id="element-3754">{biz.revenue} est. revenue</span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'contacts' && <motion.div key="contacts" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-3755">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-3756">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3757">
                  View owner & decision-maker emails
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-3758">
                  Access local business employee email lists.
                </p>
                <div className="space-y-5" data-id="element-3759">
                  <div data-id="element-3760">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3761">
                      Target titles
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3762">
                      Owner, Dentist, Founder
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-3763">
                  {contactsPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-3764">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-3765" />
                      Loading contacts...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-3766">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-3767" />
                      Emails unlocked
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-3768">
                {contactsPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-3769">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-3770" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-3771">
                      Fetching employee emails...
                    </p>
                  </div>}
                {contactsPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-3772">
                    <div className="flex items-center justify-between mb-6" data-id="element-3773">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-3774">
                        Local business contacts
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-3775">
                      {[{
                  name: 'Dr. Emily Chen',
                  role: 'Owner / Lead Dentist',
                  email: 'emily@smilebright.com'
                }, {
                  name: 'Marcus Johnson',
                  role: 'Founder',
                  email: 'mjohnson@calicoast.com'
                }, {
                  name: 'Sarah Williams',
                  role: 'Practice Manager',
                  email: 'sarah.w@bayareaortho.com'
                }].map((contact, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm flex items-center justify-between" data-id="element-3776">
                          <div data-id="element-3777">
                            <h4 className="text-base font-bold text-slate-900" data-id="element-3778">
                              {contact.name}
                            </h4>
                            <p className="text-xs text-slate-500" data-id="element-3779">
                              {contact.role}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg font-medium" data-id="element-3780">
                            <MailIcon className="w-4 h-4" data-id="element-3781" />
                            {contact.email}
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'sequence' && <motion.div key="sequence" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-3782">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-3783">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3784">
                  Launch email sequence
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-3785">
                  Send emails directly to local business contacts.
                </p>
                <div className="space-y-5" data-id="element-3786">
                  <div data-id="element-3787">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3788">
                      Campaign name
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3789">
                      CA Dentists Outreach
                    </div>
                  </div>
                  <div data-id="element-3790">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3791">
                      Sender account
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3792">
                      alex@yourcompany.com
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-3793">
                  {sequencePhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-3794">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-3795" />
                      Launching sequence...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-3796">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-3797" />
                      Sequence active
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-3798">
                {sequencePhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-3799">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-3800" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-3801">
                      Starting email sequence...
                    </p>
                  </div>}
                {sequencePhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} className="min-h-[350px]" data-id="element-3802">
                    {/* Sequence Builder Header */}
                    <div className="flex items-center justify-between mb-5" data-id="element-3803">
                      <div className="flex items-center gap-3" data-id="element-3804">
                        <h3 className="text-base font-bold text-slate-900" data-id="element-3805">
                          CA Dentists Outreach
                        </h3>
                        <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full" data-id="element-3806">
                          Draft
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-3807">
                        <button className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg" data-id="element-3808">
                          Save draft
                        </button>
                        <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5" data-id="element-3809">
                          <SendIcon className="w-3 h-3" data-id="element-3810" />
                          Start sequence
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-3811">
                      {/* Left: Sequence Steps */}
                      <div className="w-48 shrink-0" data-id="element-3812">
                        <div className="text-[10px] font-bold text-slate-400 tracking-wider uppercase mb-3" data-id="element-3813">
                          Sequence steps
                        </div>
                        <div className="space-y-2" data-id="element-3814">
                          <div className="bg-white border-2 border-blue-200 rounded-xl p-3" data-id="element-3815">
                            <div className="text-xs font-bold text-blue-600" data-id="element-3816">
                              Initial outreach
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5 truncate" data-id="element-3817">
                              Quick question about {'{'} company_...{'}'}
                            </div>
                          </div>
                          <div className="flex justify-center" data-id="element-3818">
                            <div className="w-px h-3 bg-slate-200" data-id="element-3819"></div>
                          </div>
                          <div className="bg-white border border-slate-200 rounded-xl p-3" data-id="element-3820">
                            <div className="text-xs font-semibold text-slate-700" data-id="element-3821">
                              Follow up 1
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5 truncate" data-id="element-3822">
                              Re: Quick question about {'{'} compa...{'}'}
                            </div>
                            <div className="flex items-center gap-1 mt-1.5 text-[10px] text-slate-400" data-id="element-3823">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3824">
                                <circle cx="12" cy="12" r="10" strokeWidth="2" data-id="element-3825" />
                                <path strokeWidth="2" d="M12 6v6l4 2" data-id="element-3826" />
                              </svg>
                              Wait 5 days
                            </div>
                          </div>
                          <div className="flex justify-center" data-id="element-3827">
                            <div className="w-px h-3 bg-slate-200" data-id="element-3828"></div>
                          </div>
                          <div className="border border-dashed border-slate-300 rounded-xl p-2.5 text-center" data-id="element-3829">
                            <span className="text-[10px] font-medium text-slate-400" data-id="element-3830">
                              + Add step
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Email Editor */}
                      <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3831">
                        {/* From / To */}
                        <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-3 text-xs" data-id="element-3832">
                          <span className="text-slate-400 w-8" data-id="element-3833">From:</span>
                          <span className="text-blue-600 font-medium" data-id="element-3834">
                            + Connect your email
                          </span>
                        </div>
                        <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-3 text-xs" data-id="element-3835">
                          <span className="text-slate-400 w-8" data-id="element-3836">To:</span>
                          <span className="text-slate-500" data-id="element-3837">
                            4,210 contacts from CA Dentists
                          </span>
                        </div>
                        {/* Subject */}
                        <div className="border-b border-slate-100 px-4 py-2.5" data-id="element-3838">
                          <span className="text-sm font-semibold text-slate-800" data-id="element-3839">
                            Quick question about {'{'} company_name{'}'}
                          </span>
                        </div>
                        {/* Toolbar */}
                        <div className="border-b border-slate-100 px-4 py-2 flex items-center gap-3 text-slate-400" data-id="element-3840">
                          <span className="font-bold text-xs" data-id="element-3841">B</span>
                          <span className="italic text-xs" data-id="element-3842">I</span>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3843">
                            <path strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" data-id="element-3844" />
                          </svg>
                          <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-3845"></div>
                          <span className="text-[10px] font-medium text-slate-500" data-id="element-3846">
                            {'{ }'} Insert variable
                          </span>
                        </div>
                        {/* Body */}
                        <div className="px-4 py-4 text-xs text-slate-600 leading-relaxed space-y-3" data-id="element-3847">
                          <p data-id="element-3848">
                            Hi {'{'} first_name{'}'},
                          </p>
                          <p data-id="element-3849">
                            I came across {'{'} company_name{'}'} and was
                            impressed by what you are doing in your industry.
                          </p>
                          <p data-id="element-3850">
                            I would love to connect and explore how we might be
                            able to help you streamline your operations and
                            drive more growth.
                          </p>
                          <p data-id="element-3851">
                            Would you be open to a quick 15-minute call this
                            week?
                          </p>
                          <p data-id="element-3852">
                            Best,
                            <br data-id="element-3853" />
                            {'{'} sender_name{'}'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}